import cloneDeep from "lodash/cloneDeep";
import isEmpty from "lodash/isEmpty";
import useRedirect from "@shared/hooks/useRedirect.web";
import { PUBLIC_ROUTES } from "@app-contexts/public/constants/public-routes";
import { TicketItemGroupType } from "@shared/modules/item-groups/types/ticket-item-group-type";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import { useTranslation } from "@shared/utils/internationalization";
import {
  FormattedSeatType,
  SeatingCartItemType,
} from "@shared/modules/seating/types/seating-types";
import { useCallback, useMemo } from "react";
import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";
import { SeatingCartProps } from "./SeatingCart.props";

export function useSeatingCart(props: SeatingCartProps) {
  const { overridePurchaseLimits, onCheckoutCb } = props;

  const { t } = useTranslation("seating");
  const { redirect } = useRedirect();

  const { countOfSelectedItems, seatingServiceState, seatingEventData } =
    useSeatingState();

  // Public basket is the default type
  const { itemGroups } = useBasketService();

  const minPurchaseSeatArray: FormattedSeatType[] =
    seatingServiceState.pendingCartPermissions;

  // gets the purchase limit from an override object OR an item type
  const minPurchaseLimit =
    overridePurchaseLimits?.min ??
    minPurchaseSeatArray[0]?.minPurchaseLimit ??
    undefined;

  // returns a boolean that indicates if there are pending cart items
  const minPurchaseInProgress = Boolean(
    minPurchaseLimit && minPurchaseLimit > 1 && minPurchaseSeatArray?.length > 0
  );

  const isEmptyCart =
    (!itemGroups?.length && !minPurchaseInProgress) ||
    !countOfSelectedItems ||
    countOfSelectedItems === 0;

  /*
   * Returns sorted and formatted item groups for pending seating items in the cart
   * Sorts the seats first and locations last, based on segment/location/seat identifier values
   */
  const pendingMinPurchaseSeats = useMemo(() => {
    // sort the seats based on seat identifier and location identifier
    const sortedSeatGroups = minPurchaseSeatArray.sort((a, b) => {
      // if the location is the same, sort based on seat identifier
      if (a.$data.location_identifier === b.$data.location_identifier) {
        return a.$data.identifier.localeCompare(b.$data.identifier);
      }
      // sort based on location identifier
      return a.$data.location_identifier.localeCompare(
        b.$data.location_identifier
      );
    });

    return sortedSeatGroups;
  }, [minPurchaseSeatArray]);

  const getNonWaitlistSeatsInCart = useCallback((): SeatingCartItemType[] => {
    // get item groups that have an event or membership_group value and do not have a custom package parent
    // only show items from the current seating event
    const filteredRegularItemGroups = itemGroups.filter(
      (ig) =>
        !ig.parent &&
        isEmpty(ig.waitlist_seat_segment) &&
        ((ig.event && ig.event === seatingEventData?.id) ||
          (ig.membership_group && ig.membership_group === seatingEventData?.id))
    );

    // seat groups array
    // adds permissionId, type, itemGroupId to each permission
    const flatSeatGroups: SeatingCartItemType[] = filteredRegularItemGroups
      .map((g: TicketItemGroupType) => {
        const newPermissions: SeatingCartItemType[] = [];
        g?.permissions?.forEach((perm) => {
          const copyPerm = cloneDeep(perm);
          newPermissions.push({
            ...copyPerm,
            permissionId: copyPerm.id,
            itemGroup: g,
          });
        });
        return newPermissions;
      })
      .flat();

    // sort the item groups based on seat identifier and location identifier
    const sortedSeatGroups: SeatingCartItemType[] = flatSeatGroups?.sort(
      (a, b) => {
        const locationIdentifierA = a?.seat?.location_identifier ?? "";
        const locationIdentifierB = b?.seat?.location_identifier ?? "";
        const identifierA = a?.seat?.identifier ?? "";
        const identifierB = b?.seat?.identifier ?? "";

        // if the location is the same, sort based on seat identifier
        if (locationIdentifierA === locationIdentifierB) {
          return identifierA.localeCompare(identifierB);
        }
        // sort based on location identifier
        return locationIdentifierA.localeCompare(locationIdentifierB);
      }
    );

    return sortedSeatGroups;
  }, [itemGroups, seatingEventData?.id]);

  const getWaitlistedSeatsInCart = useCallback((): SeatingCartItemType[] => {
    // get item groups that have an event or membership_group value and do not have a custom package parent
    // only show items from the current seating event
    const filteredWaitlistItemGroups = itemGroups.filter(
      (ig) =>
        !ig.parent &&
        !isEmpty(ig.waitlist_seat_segment) &&
        ((ig.event && ig.event === seatingEventData?.id) ||
          (ig.membership_group && ig.membership_group === seatingEventData?.id))
    );

    const flatWaitlistSeatGroups: SeatingCartItemType[] =
      filteredWaitlistItemGroups.map((g) => {
        return {
          id: Number(g.id),
          permissionId: -1,
          seat: {
            id: -1,
            identifier: "",
            location_identifier: "",
            is_accessible: false,
            is_limited_visibility: false,
            segment_identifier:
              g.waitlist_seat_segment?.segment_identifier ?? "",
          },
          itemGroup: g,
        };
      });

    // sort the item groups based on segment identifier
    const sortedWaitlistSeatGroups: SeatingCartItemType[] =
      flatWaitlistSeatGroups?.sort((a, b) => {
        const locationIdentifierA = a?.seat?.segment_identifier ?? "";
        const locationIdentifierB = b?.seat?.segment_identifier ?? "";

        // sort based on segment identifier
        return locationIdentifierA.localeCompare(locationIdentifierB);
      });

    return sortedWaitlistSeatGroups;
  }, [itemGroups, seatingEventData?.id]);

  const getLocationsInCart = useCallback((): SeatingCartItemType[] => {
    // get item groups that have an event or membership_group value and do not have a custom package parent
    // only show items from the current seating event
    const filteredRegularItemGroups = itemGroups.filter(
      (ig) =>
        !ig.parent &&
        isEmpty(ig.waitlist_seat_segment) &&
        ((ig.event && ig.event === seatingEventData?.id) ||
          (ig.membership_group && ig.membership_group === seatingEventData?.id))
    );

    // location groups array
    // adds permissionId, type, itemGroupId to each permission
    const flatLocGroups: SeatingCartItemType[] = filteredRegularItemGroups
      .map((g: TicketItemGroupType) => {
        const newPermissions: SeatingCartItemType[] = [];
        g?.location_permissions?.forEach((perm) => {
          const copyPerm = cloneDeep(perm);
          newPermissions.push({
            ...copyPerm,
            permissionId: copyPerm.id,
            itemGroup: g,
          });
        });
        return newPermissions;
      })
      .flat();

    // sort the item groups based on segment identifier and location identifier
    const sortedLocGroups: SeatingCartItemType[] = flatLocGroups?.sort(
      (a, b) => {
        const segmentIdentifierA = a?.location?.segment_identifier ?? "";
        const segmentIdentifierB = b?.location?.segment_identifier ?? "";
        const locationIdentifierA = a?.location?.identifier ?? "";
        const locationIdentifierB = b?.location?.identifier ?? "";

        // if the segment is the same, sort based on location identifier
        if (segmentIdentifierA === segmentIdentifierB) {
          return locationIdentifierA.localeCompare(locationIdentifierB);
        }

        // sort based on segment identifier
        return segmentIdentifierA.localeCompare(segmentIdentifierB);
      }
    );

    return sortedLocGroups;
  }, [itemGroups, seatingEventData?.id]);

  /*
   * Returns sorted and formatted item groups for seating items in the cart
   * Sorts the seats first and locations last, based on segment/location/seat identifier values
   */
  const currentMapItemsInCart = useMemo(() => {
    if (!itemGroups || !itemGroups.length) {
      return [];
    }

    const sortedWaitlistSeats = getWaitlistedSeatsInCart();
    const sortedSeatGroups = getNonWaitlistSeatsInCart();
    const sortedLocGroups = getLocationsInCart();

    // put seat items first and location items last
    const sortedMapItems: SeatingCartItemType[] = sortedWaitlistSeats
      .concat(sortedSeatGroups)
      .concat(sortedLocGroups);

    return sortedMapItems;
  }, [
    itemGroups,
    getNonWaitlistSeatsInCart,
    getLocationsInCart,
    getWaitlistedSeatsInCart,
  ]);

  const onCheckout = useCallback(() => {
    if (onCheckoutCb) {
      onCheckoutCb();
    } else {
      redirect(PUBLIC_ROUTES.CHECKOUT.ROOT);
    }
  }, [redirect, onCheckoutCb]);

  const checkoutTooltipText = useMemo(() => {
    if (minPurchaseInProgress) {
      return `${t("Minimum {{count}} items required", {
        count: minPurchaseLimit,
      })}`;
    }
    return "";
  }, [t, minPurchaseInProgress]);

  return {
    isEmptyCart,
    pendingMinPurchaseSeats,
    currentMapItemsInCart,
    minPurchaseSeatArray,
    minPurchaseLimit,
    minPurchaseInProgress,
    checkoutTooltipText,
    onCheckout,
  };
}
